/**
 * 테이블 인터랙션 커스텀 훅
 * - 테이블/셀 클릭 진입, Shift+클릭, 드래그 다중 셀 선택
 * - 경계선 리사이즈 (내부/외곽)
 * - 셀 여백 클릭 커서 배치
 * - 호버 커서 변경
 */
import { useEffect, useRef } from 'react';
import useEditorStore from '../stores/useEditorStore';
import {
  highlightTableCells,
  hitTestTableCell,
  expandCellSelection,
  enterTableEditMode,
  hitTestTableBorder,
  recalcTableCells,
  getActualTextHeight,
} from '../utils/tableUtils';
import { DEFAULT_TEXT_INSET, TABLE_MIN_MARGIN, TABLE_MIN_COL_WIDTH } from '../constants/editorConstants';

// === 경계선 드래그 처리 ===

const handleBorderDrag_col = (borderDrag, pointer, scaleX, canvas) => {
  const localX = (pointer.x - borderDrag.tableGroup.getCenterPoint().x) / scaleX;
  const delta = localX - borderDrag.startPos;
  const colWidths = borderDrag.tableGroup._tableColWidths;
  const minW = TABLE_MIN_COL_WIDTH;
  // 총 너비 보존: 양방향 동시 클램핑
  const totalW = colWidths[borderDrag.index] + colWidths[borderDrag.index + 1];
  const maxLeft = totalW - minW;
  const newLeft = Math.max(minW, Math.min(maxLeft, colWidths[borderDrag.index] + delta));
  const newRight = totalW - newLeft;
  const deltaLeft = newLeft - colWidths[borderDrag.index];
  if (Math.abs(deltaLeft) > 0.1) {
    colWidths[borderDrag.index] = newLeft;
    colWidths[borderDrag.index + 1] = newRight;
    borderDrag.startPos = localX;
    recalcTableCells(borderDrag.tableGroup);
    canvas.requestRenderAll();
  }
};

const MIN_MARGIN = TABLE_MIN_MARGIN;

/**
 * 행의 최소 높이를 텍스트 기반으로 동적 계산
 * = 해당 행에서 가장 큰 텍스트 높이 + MIN_MARGIN * 2
 */
const getMinRowHeight = (tg, rowIdx) => {
  const texts = tg.getObjects().filter(c => c._isTableCellText && c._cellRow === rowIdx && (c._rowSpan || 1) <= 1);
  let maxTextH = 0;
  for (const text of texts) {
    if (text.initDimensions) text.initDimensions();
    maxTextH = Math.max(maxTextH, getActualTextHeight(text));
  }
  return Math.max(15, maxTextH + MIN_MARGIN * 2);
};

const updateRowMargins = (tg, rowIdx, delta) => {
  const rects = tg.getObjects().filter(c => c._isTableCell && c._cellRow === rowIdx);
  const halfDelta = delta / 2;
  for (const rect of rects) {
    const curT = rect._marT ?? DEFAULT_TEXT_INSET.T;
    const curB = rect._marB ?? DEFAULT_TEXT_INSET.B;
    rect._marT = Math.max(MIN_MARGIN, curT + halfDelta);
    rect._marB = Math.max(MIN_MARGIN, curB + halfDelta);
  }
};

const handleBorderDrag_row = (borderDrag, pointer, scaleY, canvas) => {
  const localY = (pointer.y - borderDrag.tableGroup.getCenterPoint().y) / scaleY;
  const delta = localY - borderDrag.startPos;
  const tg = borderDrag.tableGroup;
  const rowHeights = tg._tableRowHeights;
  const idx = borderDrag.index;
  const minH = getMinRowHeight(tg, idx);
  // 위쪽 행만 변경, 아래쪽 행은 유지 → 테이블 전체 높이가 변경됨
  const newHeight = Math.max(minH, rowHeights[idx] + delta);
  const actualChange = newHeight - rowHeights[idx];
  if (Math.abs(actualChange) > 0.1) {
    rowHeights[idx] = newHeight;
    updateRowMargins(tg, idx, actualChange);
    const savedLeft = tg.left;
    const savedTop = tg.top;
    recalcTableCells(tg);
    // 위쪽 고정: 전체 높이 변경분의 절반만큼 아래로 이동
    tg.left = savedLeft;
    tg.top = savedTop + (actualChange * scaleY) / 2;
    tg.setCoords();
    borderDrag.startPos = localY + actualChange / 2;
    canvas.requestRenderAll();
  }
};

const handleBorderDrag_edgeCol = (borderDrag, pointer, scaleX, canvas) => {
  const sceneDeltaX = pointer.x - borderDrag.startSceneX;
  const localDelta = sceneDeltaX / scaleX;
  const colWidths = borderDrag.tableGroup._tableColWidths;
  const idx = borderDrag.index;
  const minW = TABLE_MIN_COL_WIDTH;
  const widthDelta = borderDrag.type === 'edge-left' ? -localDelta : localDelta;
  const newWidth = Math.max(minW, colWidths[idx] + widthDelta);
  const actualChange = newWidth - colWidths[idx];
  if (Math.abs(actualChange) > 0.1) {
    colWidths[idx] = newWidth;
    const savedLeft = borderDrag.tableGroup.left;
    const savedTop = borderDrag.tableGroup.top;
    recalcTableCells(borderDrag.tableGroup);
    if (borderDrag.type === 'edge-left') {
      borderDrag.tableGroup.left = savedLeft - (actualChange * scaleX) / 2;
    } else {
      borderDrag.tableGroup.left = savedLeft + (actualChange * scaleX) / 2;
    }
    borderDrag.tableGroup.top = savedTop;
    borderDrag.tableGroup.setCoords();
    borderDrag.startSceneX = pointer.x;
    canvas.requestRenderAll();
  }
};

const handleBorderDrag_edgeRow = (borderDrag, pointer, scaleY, canvas) => {
  const sceneDeltaY = pointer.y - borderDrag.startSceneY;
  const localDelta = sceneDeltaY / scaleY;
  const rowHeights = borderDrag.tableGroup._tableRowHeights;
  const idx = borderDrag.index;
  const minH = getMinRowHeight(borderDrag.tableGroup, idx);
  const heightDelta = borderDrag.type === 'edge-top' ? -localDelta : localDelta;
  const newHeight = Math.max(minH, rowHeights[idx] + heightDelta);
  const actualChange = newHeight - rowHeights[idx];
  if (Math.abs(actualChange) > 0.1) {
    rowHeights[idx] = newHeight;
    updateRowMargins(borderDrag.tableGroup, idx, actualChange);
    const savedLeft = borderDrag.tableGroup.left;
    const savedTop = borderDrag.tableGroup.top;
    recalcTableCells(borderDrag.tableGroup);
    borderDrag.tableGroup.left = savedLeft;
    if (borderDrag.type === 'edge-top') {
      borderDrag.tableGroup.top = savedTop - (actualChange * scaleY) / 2;
    } else {
      borderDrag.tableGroup.top = savedTop + (actualChange * scaleY) / 2;
    }
    borderDrag.tableGroup.setCoords();
    borderDrag.startSceneY = pointer.y;
    canvas.requestRenderAll();
  }
};

// === 유틸 ===

const getScenePointer = (e, canvas) => {
  const z = canvas.getZoom ? canvas.getZoom() : 1;
  return e.scenePoint || { x: e.e.offsetX / z, y: e.e.offsetY / z };
};

const startBorderDrag = (tableGroup, borderHit, e, canvas, extra = {}) => {
  const scenePointer = getScenePointer(e, canvas);
  return {
    tableGroup,
    type: borderHit.type,
    index: borderHit.index,
    startPos: borderHit.localPos,
    startSceneX: scenePointer.x,
    startSceneY: scenePointer.y,
    ...extra,
  };
};

// === 메인 훅 ===

const useTableInteraction = (canvas, pushHistory) => {
  const tableDragRef = useRef(null);
  const tableBorderDragRef = useRef(null);
  const prevActiveObjRef = useRef(null);
  const tableClickPendingRef = useRef(null);

  useEffect(() => {
    if (!canvas) return;

    const handleMouseDownBefore = () => {
      prevActiveObjRef.current = canvas.getActiveObject();
    };

    const handleMouseDown = (e) => {
      const store = useEditorStore.getState();
      const currentEditingGroupId = store.editingGroupId;

      // --- 테이블 선택 상태에서 재클릭 ---
      if (!currentEditingGroupId && e.target?.type === 'group' && e.target._isTableGroup) {
        if (prevActiveObjRef.current === e.target) {
          // 경계선 리사이즈 감지
          const borderHit = hitTestTableBorder(e.target, e, canvas);
          if (borderHit) {
            e.target.set({ lockMovementX: true, lockMovementY: true });
            tableBorderDragRef.current = startBorderDrag(e.target, borderHit, e, canvas, { wasTableSelect: true });
            e.e.preventDefault();
            e.e.stopPropagation();
            return;
          }
          // 셀 편집 진입 대기 (mouse:up에서 처리, 드래그 이동 허용)
          tableClickPendingRef.current = {
            tableGroup: e.target, event: e,
            startX: e.e.clientX, startY: e.e.clientY,
          };
          return;
        }
      }

      if (!currentEditingGroupId) return;

      // --- 셀 편집 모드: 외부 클릭 → 편집 종료 ---
      const target = e.target;
      if (!target) {
        store.exitGroupEditing();
        store.clearTableCellSelection();
        return;
      }
      const editingGroup = canvas.getObjects().find(o => o.id === currentEditingGroupId);
      if (editingGroup && target !== editingGroup) {
        if (!editingGroup.getObjects().includes(target)) {
          store.exitGroupEditing();
          store.clearTableCellSelection();
          return;
        }
      }
      if (!editingGroup || !editingGroup._isTableGroup) return;

      // --- 경계선 리사이즈 시작 ---
      const borderHit = hitTestTableBorder(editingGroup, e, canvas);
      if (borderHit) {
        tableBorderDragRef.current = startBorderDrag(editingGroup, borderHit, e, canvas);
        const activeObj = canvas.getActiveObject();
        if (activeObj?.isEditing) activeObj.exitEditing();
        e.e.preventDefault();
        e.e.stopPropagation();
        return;
      }

      // --- Shift+클릭: 사각형 영역 다중 셀 선택 ---
      if (e.e.shiftKey) {
        const hitCell = hitTestTableCell(editingGroup, e, canvas);
        if (hitCell && store.selectedTableCells.length > 0) {
          const rectCells = expandCellSelection(store.selectedTableCells[0], hitCell);
          const activeObj = canvas.getActiveObject();
          if (activeObj?.isEditing) activeObj.exitEditing();
          canvas.discardActiveObject();
          store.setSelectedTableCells(rectCells);
          store.setSelectedTableGroup(editingGroup);
          highlightTableCells(editingGroup, rectCells);
          canvas.requestRenderAll();
          e.e.preventDefault();
          e.e.stopPropagation();
        }
        return;
      }

      // --- 셀 클릭: 여백 감지 + 드래그 시작 ---
      const hitCell = hitTestTableCell(editingGroup, e, canvas);
      if (hitCell) {
        const textChild = editingGroup.getObjects().find(
          ch => ch._isTableCellText && ch._cellRow === hitCell.row && ch._cellCol === hitCell.col
        );
        // 셀 여백(텍스트박스 위/아래) 클릭 처리
        if (textChild) {
          const pointer = getScenePointer(e, canvas);
          const groupCenter = editingGroup.getCenterPoint();
          const scaleY = editingGroup.scaleY || 1;
          const localY = (pointer.y - groupCenter.y) / scaleY;
          const textHalfH = (textChild.height || 0) / 2;
          const textTopEdge = textChild.top - textHalfH;
          const textBottomEdge = textChild.top + textHalfH;

          if (localY < textTopEdge || localY > textBottomEdge) {
            // 여백 클릭: 다른 셀이면 전환
            if (!textChild.isEditing) {
              canvas.setActiveObject(textChild);
            }
            // 전체 선택 상태면 해제 후 커서 배치
            const textLen = (textChild.text || '').length;
            if (textChild.selectionStart === 0 && textChild.selectionEnd === textLen && textLen > 0) {
              textChild.selectionStart = textChild.selectionEnd =
                localY < textTopEdge ? 0 : textLen;
              canvas.requestRenderAll();
            }
            e.e.preventDefault();
            return;
          }
        }
        tableDragRef.current = { tableGroup: editingGroup, startCell: hitCell, isDragging: false };
      }
    };

    const handleMouseMove = (e) => {
      // --- 경계선 드래그 ---
      const borderDrag = tableBorderDragRef.current;
      if (borderDrag) {
        const pointer = getScenePointer(e, canvas);
        const scaleX = borderDrag.tableGroup.scaleX || 1;
        const scaleY = borderDrag.tableGroup.scaleY || 1;
        const { type } = borderDrag;
        if (type === 'col') handleBorderDrag_col(borderDrag, pointer, scaleX, canvas);
        else if (type === 'row') handleBorderDrag_row(borderDrag, pointer, scaleY, canvas);
        else if (type === 'edge-left' || type === 'edge-right') handleBorderDrag_edgeCol(borderDrag, pointer, scaleX, canvas);
        else if (type === 'edge-top' || type === 'edge-bottom') handleBorderDrag_edgeRow(borderDrag, pointer, scaleY, canvas);
        return;
      }

      // --- 경계선 호버 커서 ---
      const store2 = useEditorStore.getState();
      let hoverTable = null;
      if (store2.editingGroupId) {
        hoverTable = canvas.getObjects().find(o => o.id === store2.editingGroupId);
      } else {
        const activeObj = canvas.getActiveObject();
        if (activeObj?._isTableGroup) hoverTable = activeObj;
      }
      if (hoverTable?._isTableGroup) {
        const bHit = hitTestTableBorder(hoverTable, e, canvas);
        const upperEl = canvas.upperCanvasEl || canvas.wrapperEl;
        if (bHit) {
          const isColType = bHit.type === 'col' || bHit.type === 'edge-left' || bHit.type === 'edge-right';
          upperEl.style.cursor = isColType ? 'col-resize' : 'row-resize';
        } else if (upperEl.style.cursor === 'col-resize' || upperEl.style.cursor === 'row-resize') {
          upperEl.style.cursor = '';
        }
      }

      // --- 드래그 셀 선택 ---
      const drag = tableDragRef.current;
      if (!drag) return;
      const hitCell = hitTestTableCell(drag.tableGroup, e, canvas);
      if (!hitCell) return;
      if (hitCell.row === drag.startCell.row && hitCell.col === drag.startCell.col) return;
      if (!drag.isDragging) {
        drag.isDragging = true;
        const activeObj = canvas.getActiveObject();
        if (activeObj?.isEditing) activeObj.exitEditing();
        canvas.discardActiveObject();
      }
      const rectCells = expandCellSelection(drag.startCell, hitCell);
      const store = useEditorStore.getState();
      store.setSelectedTableCells(rectCells);
      store.setSelectedTableGroup(drag.tableGroup);
      highlightTableCells(drag.tableGroup, rectCells);
      canvas.requestRenderAll();
    };

    const handleMouseUp = (e) => {
      // --- 경계선 드래그 종료 ---
      if (tableBorderDragRef.current) {
        const drag = tableBorderDragRef.current;
        if (drag.wasTableSelect) {
          drag.tableGroup.set({ lockMovementX: false, lockMovementY: false });
        }
        pushHistory();
        tableBorderDragRef.current = null;
        const upperEl = canvas.upperCanvasEl || canvas.wrapperEl;
        upperEl.style.cursor = '';
      }
      tableDragRef.current = null;

      // --- 테이블 재클릭 → 셀 편집 진입 ---
      const pending = tableClickPendingRef.current;
      if (pending) {
        tableClickPendingRef.current = null;
        const dx = e.e.clientX - pending.startX;
        const dy = e.e.clientY - pending.startY;
        if (Math.abs(dx) <= 5 && Math.abs(dy) <= 5) {
          const tableGroup = pending.tableGroup;
          enterTableEditMode(tableGroup);
          const hitCell = hitTestTableCell(tableGroup, pending.event, canvas);
          let textChild = hitCell
            ? tableGroup.getObjects().find(ch => ch._isTableCellText && ch._cellRow === hitCell.row && ch._cellCol === hitCell.col)
            : null;
          if (!textChild) textChild = tableGroup.getObjects().find(ch => ch._isTableCellText);
          if (textChild) {
            const cellPos = { row: textChild._cellRow, col: textChild._cellCol };
            highlightTableCells(tableGroup, [cellPos]);
            const store = useEditorStore.getState();
            store.setEditingGroupId(tableGroup.id);
            store.setSelectedTableGroup(tableGroup);
            store.setSelectedTableCells([cellPos]);
            canvas.setActiveObject(textChild);
            canvas.requestRenderAll();
            if (textChild.enterEditing) {
              textChild.enterEditing();
              const len = (textChild.text || '').length;
              textChild.selectionStart = len;
              textChild.selectionEnd = len;
            }
          }
        }
      }
    };

    canvas.on('mouse:down:before', handleMouseDownBefore);
    canvas.on('mouse:down', handleMouseDown);
    canvas.on('mouse:move', handleMouseMove);
    canvas.on('mouse:up', handleMouseUp);

    return () => {
      canvas.off('mouse:down:before', handleMouseDownBefore);
      canvas.off('mouse:down', handleMouseDown);
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:up', handleMouseUp);
    };
  }, [canvas, pushHistory]);

  return { tableBorderDragRef };
};

export default useTableInteraction;
